import { call, delay, fork, select, take } from 'redux-saga/effects';
import type { ReaderTaskType } from '@dynamika-shared/types/reader';

import { selectCachedSubtaskIds } from '../../readerCache/readerCache.slice';
import { readerTaskSlice, actions as readerTaskActions } from '../../readerTask/readerTask.slice';
import { DICOMViewerSlice } from '../../DICOMViewer/DICOMViewer.slice';
import { fetchReaderTaskWorker } from '../../readerTask/readerTask.saga';
import type { ReaderTaskFetchAction } from '../../readerTask/readerTask.types';

/**
 * 📋 lazyPrefetchSubtasksWorker — quietly pre-loads the OTHER visits of the same
 *    reading task in the background, so later visit-switches feel instant.
 *
 * WHY: a reading task has many sibling visits. We deliberately do NOT load them
 *      all up front (that would slow the first open). Instead, after the reader's
 *      FIRST manual visit-switch, we fetch the remaining visits during browser
 *      idle time so they're already cached when the reader reaches them.
 *
 * - Runs at most once per parent task (guarded by `firedForParent`).
 * - Only fetches visits not already in the cache.
 * - Uses requestIdleCallback + a small gap between fetches so it never blocks
 *   the UI or the current switch.
 *
 * `watchFirstVisitSwitchSaga` waits for: initial task load → first manual switch
 * → viewer open, then kicks this off.
 */
const firedForParent = new Set<string>();

export const requestIdle = (): Promise<void> =>
  new Promise(resolve => {
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
    };
    const ric = w.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 0));
    ric(() => resolve(), { timeout: 5000 });
  });

export function* lazyPrefetchSubtasksWorker(parentTaskId: string, clinicalTrialId: string) {
  if (firedForParent.has(parentTaskId)) {
    return;
  }
  firedForParent.add(parentTaskId);

  const parentTask: Partial<ReaderTaskType> | null | undefined = yield select(
    readerTaskSlice.selectors.selectReaderTask
  );
  const childTasks: Array<{ id: string }> = parentTask?.childTasks ?? [];
  if (childTasks.length === 0) {
    return;
  }

  const cachedIds: Set<string> = yield select(selectCachedSubtaskIds, parentTaskId);

  const absent = childTasks.filter(ct => !cachedIds.has(ct.id));
  if (absent.length === 0) {
    return;
  }

  for (const ct of absent) {
    yield call(requestIdle);
    yield delay(150);
    yield call(fetchReaderTaskWorker, {
      type: readerTaskActions.fetchReaderTask.type,
      payload: { clinicalTrialId, taskId: ct.id, parentTaskId },
    } as ReaderTaskFetchAction);
  }
}

export function* watchFirstVisitSwitchSaga() {
  yield take(readerTaskActions.fetchReaderTaskSuccess.type);
  yield take(readerTaskActions.fetchReaderTask.type);
  yield take(DICOMViewerSlice.actions.openViewer.type);
  yield delay(2000);

  const parentTask: Partial<ReaderTaskType> | null | undefined = yield select(
    readerTaskSlice.selectors.selectReaderTask
  );
  if (!parentTask?.id || !parentTask?.clinicalTrialId) {
    return;
  }
  yield fork(lazyPrefetchSubtasksWorker, parentTask.id, parentTask.clinicalTrialId);
}

export const _resetForTests = (): void => {
  firedForParent.clear();
};
